/*
 * Spares users from writing glob wildcards for the common pathname exclusions: they pick how
 * the text should match and type plain text, and the mode supplies the wildcards. Only the
 * advanced mode passes the typed text through as a glob of its own. The reverse inference lets
 * every stored glob be described in the same plain-text terms it could have been entered in.
 */

export const PathnameMatchModes = {
  Contains: 'contains',
  Exactly: 'exactly',
  StartsWith: 'starts-with',
  EndsWith: 'ends-with',
  Advanced: 'advanced',
} as const;

export type PathnameMatchMode = typeof PathnameMatchModes[keyof typeof PathnameMatchModes];

export const DEFAULT_PATHNAME_MATCH_MODE: PathnameMatchMode = PathnameMatchModes.Contains;

export const PATHNAME_MATCH_MODE_ORDER: readonly PathnameMatchMode[] = [
  PathnameMatchModes.Contains,
  PathnameMatchModes.Exactly,
  PathnameMatchModes.StartsWith,
  PathnameMatchModes.EndsWith,
  PathnameMatchModes.Advanced,
];

const GLOB_WILDCARD_PATTERN = /[*?]/;

/** Globs have no escape syntax, so plain-text modes cannot carry a literal `*` or `?`. */
export const hasGlobWildcard = (text: string): boolean => GLOB_WILDCARD_PATTERN.test(text);

export const toPathnameGlob = (mode: PathnameMatchMode, text: string): string => {
  switch (mode) {
    case PathnameMatchModes.Contains:
      return `*${text}*`;
    case PathnameMatchModes.StartsWith:
      return `${text}*`;
    case PathnameMatchModes.EndsWith:
      return `*${text}`;
    case PathnameMatchModes.Exactly:
    case PathnameMatchModes.Advanced:
      return text;
  }
};

export interface PathnameMatch {
  readonly mode: PathnameMatchMode;
  readonly text: string;
}

/** Tried in order; Exactly is last so any glob with a wildcard falls through to Advanced. */
const PLAIN_TEXT_GLOB_PATTERNS: ReadonlyArray<readonly [PathnameMatchMode, RegExp]> = [
  [PathnameMatchModes.Contains, /^\*([^*?]+)\*$/],
  [PathnameMatchModes.StartsWith, /^([^*?]+)\*$/],
  [PathnameMatchModes.EndsWith, /^\*([^*?]+)$/],
  [PathnameMatchModes.Exactly, /^([^*?]*)$/],
];

/** Inverts `toPathnameGlob`: `toPathnameGlob(mode, text)` always reproduces the given glob. */
export const fromPathnameGlob = (glob: string): PathnameMatch => {
  for (const [mode, pattern] of PLAIN_TEXT_GLOB_PATTERNS) {
    const text = pattern.exec(glob)?.[1];
    if (text !== undefined) return { mode, text };
  }
  return { mode: PathnameMatchModes.Advanced, text: glob };
};
